import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Button } from '../../components/design-system';
import {
  KIND_LABEL, allowedActions, optionKey,
  type AdminQuestion, type QuestionKind, type QuestionOption,
} from '../../lib/tripSurvey';
import { cn } from '../../lib/utils';

interface Draft {
  /** Set when editing an unused question in place; null for a new question or a replacement. */
  editId: string | null;
  /** The used question this one replaces (archived once the new one is saved). */
  replaces: AdminQuestion | null;
  kind: QuestionKind;
  label: string;
  hint: string;
  section: string;
  required: boolean;
  hostVisible: boolean;
  /** choice / multi: one option per line. yesno: line 1 = yes label, line 2 = no label. */
  optionsText: string;
  followupOn: boolean;
  followupPlaceholder: string;
  /** Option keys that open the follow-up; ['*'] = any. */
  followupFor: string[];
  role: AdminQuestion['role'];
}

const KINDS = Object.keys(KIND_LABEL) as QuestionKind[];
const NEEDS_OPTIONS: QuestionKind[] = ['choice', 'multi'];

function draftFrom(q: AdminQuestion | null, mode: 'new' | 'edit' | 'replace', defaultSection: string): Draft {
  if (!q) {
    return { editId: null, replaces: null, kind: 'stars', label: '', hint: '', section: defaultSection, required: false, hostVisible: false, optionsText: '', followupOn: false, followupPlaceholder: '', followupFor: ['*'], role: null };
  }
  let optionsText = q.options.map((o) => o.label).join('\n');
  if (q.kind === 'yesno') {
    const yes = q.options.find((o) => o.key === 'yes')?.label ?? 'Yes';
    const no = q.options.find((o) => o.key === 'no')?.label ?? 'No';
    optionsText = `${yes}\n${no}`;
  }
  return {
    editId: mode === 'edit' ? q.id : null,
    replaces: mode === 'replace' ? q : null,
    kind: q.kind,
    label: q.label,
    hint: q.hint ?? '',
    section: q.section,
    required: q.required,
    hostVisible: q.host_visible,
    optionsText,
    followupOn: !!q.followup,
    followupPlaceholder: q.followup?.placeholder ?? '',
    followupFor: q.followup?.on ?? ['*'],
    role: mode === 'edit' ? q.role : null,
  };
}

/** The options the form describes, with stable keys (yes/no keep their keys). */
function optionsFrom(d: Draft): QuestionOption[] {
  const lines = d.optionsText.split('\n').map((l) => l.trim()).filter(Boolean);
  if (d.kind === 'yesno') return [{ key: 'yes', label: lines[0] || 'Yes' }, { key: 'no', label: lines[1] || 'No' }];
  if (!NEEDS_OPTIONS.includes(d.kind)) return [];
  const keys: string[] = [];
  return lines.map((label) => {
    const key = optionKey(label, keys);
    keys.push(key);
    return { key, label };
  });
}

function validate(d: Draft): string | null {
  if (!d.label.trim()) return 'Write the question.';
  if (!d.section.trim()) return 'Pick a section.';
  if (NEEDS_OPTIONS.includes(d.kind) && optionsFrom(d).length < 2) return 'Add at least two options, one per line.';
  if (d.followupOn && d.kind !== 'text' && d.kind !== 'stars' && d.kind !== 'scale10' && d.followupFor.length === 0) return 'Pick which answers open the text box.';
  return null;
}

function QuestionForm({ draft, sections, saving, error, onChange, onSave, onCancel }: {
  draft: Draft;
  sections: string[];
  saving: boolean;
  error: string | null;
  onChange: (d: Draft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const options = optionsFrom(draft);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => onChange({ ...draft, [k]: v });
  const isCore = draft.role !== null;
  const input = 'w-full rounded-lg border border-app-border bg-app-bg px-3 py-2 text-sm text-app-fg focus:border-gold focus:outline-none';
  const choiceKind = draft.kind === 'choice' || draft.kind === 'multi' || draft.kind === 'yesno';

  return (
    <div className="bg-app-card border border-gold/40 rounded-xl p-4 mb-4">
      <h3 className="text-sm font-semibold text-app-fg mb-1">
        {draft.replaces ? 'Replace question' : draft.editId ? 'Edit question' : 'New question'}
      </h3>
      {draft.replaces && (
        <p className="text-xs text-app-muted mb-3">
          “{draft.replaces.label}” has answers, so it can't change. Saving archives it (its answers stay in the results) and
          starts this new version in its place.
        </p>
      )}
      <div className="grid gap-3">
        <label className="text-xs text-app-muted">
          Type
          <select value={draft.kind} onChange={(e) => set('kind', e.target.value as QuestionKind)} disabled={isCore} className={cn(input, 'mt-1')}>
            {KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
          </select>
        </label>
        <label className="text-xs text-app-muted">
          Question <span className="text-app-muted/70">(use {'{host}'} and {'{trip}'} for the host's name and the trip)</span>
          <input value={draft.label} onChange={(e) => set('label', e.target.value)} maxLength={300} className={cn(input, 'mt-1')} />
        </label>
        <label className="text-xs text-app-muted">
          Hint under the question <span className="text-app-muted/70">(optional)</span>
          <input value={draft.hint} onChange={(e) => set('hint', e.target.value)} maxLength={300} className={cn(input, 'mt-1')} />
        </label>
        <label className="text-xs text-app-muted">
          Section heading
          <input value={draft.section} onChange={(e) => set('section', e.target.value)} list="survey-sections" maxLength={80} className={cn(input, 'mt-1')} />
          <datalist id="survey-sections">{sections.map((s) => <option key={s} value={s} />)}</datalist>
        </label>

        {draft.kind === 'yesno' && (
          <label className="text-xs text-app-muted">
            Button labels, “yes” first then “no”, one per line
            <textarea value={draft.optionsText} onChange={(e) => set('optionsText', e.target.value)} rows={2} className={cn(input, 'mt-1 resize-none')} />
          </label>
        )}
        {NEEDS_OPTIONS.includes(draft.kind) && (
          <label className="text-xs text-app-muted">
            Options, one per line
            <textarea value={draft.optionsText} onChange={(e) => set('optionsText', e.target.value)} rows={4} className={cn(input, 'mt-1 resize-none')} />
          </label>
        )}

        <label className="flex items-center gap-2 text-sm text-app-fg">
          <input type="checkbox" checked={draft.required} onChange={(e) => set('required', e.target.checked)} />
          Required
        </label>

        <label className="flex items-start gap-2 text-sm text-app-fg">
          <input type="checkbox" className="mt-1" checked={draft.hostVisible} onChange={(e) => set('hostVisible', e.target.checked)} />
          <span>
            Show the answer to the host
            <span className="block text-[11px] text-app-muted">Only when the traveler agreed to share. Use it for ratings and comments about the trip, never the app or price. Locked once the question has answers.</span>
          </span>
        </label>

        <div>
          <label className="flex items-center gap-2 text-sm text-app-fg">
            <input type="checkbox" checked={draft.followupOn} onChange={(e) => set('followupOn', e.target.checked)} />
            Open a text box under the question
          </label>
          {draft.followupOn && (
            <div className="mt-2 grid gap-2 pl-6">
              <input value={draft.followupPlaceholder} onChange={(e) => set('followupPlaceholder', e.target.value)} maxLength={200}
                placeholder="What the box asks, e.g. What happened?" className={input} />
              {choiceKind && (
                <div className="flex flex-wrap gap-2 items-center text-xs text-app-muted">
                  Open it for:
                  <button type="button" onClick={() => set('followupFor', ['*'])}
                    className={cn('px-3 py-1.5 rounded-full border', draft.followupFor.includes('*') ? 'bg-app-fg text-app-bg border-app-fg' : 'border-app-border')}>
                    Any answer
                  </button>
                  {options.map((o) => {
                    const on = draft.followupFor.includes(o.key);
                    return (
                      <button key={o.key} type="button"
                        onClick={() => set('followupFor', on ? draft.followupFor.filter((k) => k !== o.key) : [...draft.followupFor.filter((k) => k !== '*'), o.key])}
                        className={cn('px-3 py-1.5 rounded-full border', on ? 'bg-app-fg text-app-bg border-app-fg' : 'border-app-border')}>
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {error && <p className="text-sm text-crimson mt-3" role="alert">{error}</p>}
      <div className="flex gap-2 mt-4">
        <Button onClick={onSave} disabled={saving}>{saving ? 'Saving…' : draft.replaces ? 'Save new version' : 'Save question'}</Button>
        <Button variant="secondary" onClick={onCancel} disabled={saving}>Cancel</Button>
      </div>
    </div>
  );
}

export function SurveyQuestionEditor({ questions, onChanged }: { questions: AdminQuestion[]; onChanged: () => Promise<void> }) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const active = useMemo(() => questions.filter((q) => !q.archived_at), [questions]);
  const archived = useMemo(() => questions.filter((q) => q.archived_at), [questions]);
  const sections = useMemo(() => [...new Set(questions.map((q) => q.section))], [questions]);

  async function run(fn: () => Promise<{ error: { message: string } | null }>, done?: string) {
    setSaving(true);
    setError(null);
    const { error: err } = await fn();
    if (err) { setError(err.message); setSaving(false); return false; }
    await onChanged();
    setSaving(false);
    if (done) setNotice(done);
    return true;
  }

  async function save() {
    if (!draft) return;
    const problem = validate(draft);
    if (problem) { setError(problem); return; }
    const options = optionsFrom(draft);
    const followup = draft.followupOn
      ? {
          on: draft.kind === 'text' || draft.kind === 'stars' || draft.kind === 'scale10' ? ['*'] : draft.followupFor,
          ...(draft.followupPlaceholder.trim() ? { placeholder: draft.followupPlaceholder.trim() } : {}),
        }
      : null;
    const fields = {
      kind: draft.kind, label: draft.label.trim(), hint: draft.hint.trim() || null, options, required: draft.required, host_visible: draft.hostVisible,
      section: draft.section.trim(), followup,
    };

    const ok = await run(async () => {
      if (draft.editId) return supabase.from('survey_questions').update(fields).eq('id', draft.editId);
      const position = draft.replaces ? draft.replaces.position : (active[active.length - 1]?.position ?? 0) + 10;
      const inserted = await supabase.from('survey_questions').insert({ ...fields, position });
      if (inserted.error || !draft.replaces) return inserted;
      // The new version is in; now retire the one it replaces.
      return supabase.from('survey_questions').update({ archived_at: new Date().toISOString() }).eq('id', draft.replaces.id);
    }, draft.replaces ? 'New version saved. The old question is archived.' : 'Question saved.');
    if (ok) setDraft(null);
  }

  async function move(index: number, dir: -1 | 1) {
    const order = [...active];
    const j = index + dir;
    if (j < 0 || j >= order.length) return;
    [order[index], order[j]] = [order[j], order[index]];
    await run(async () => {
      for (let i = 0; i < order.length; i++) {
        const position = (i + 1) * 10;
        if (order[i].position === position) continue;
        const { error: err } = await supabase.from('survey_questions').update({ position }).eq('id', order[i].id);
        if (err) return { error: err };
      }
      return { error: null };
    });
  }

  const del = (q: AdminQuestion) => {
    if (!window.confirm(`Delete “${q.label}”? Nobody has answered it, so nothing is lost.`)) return;
    void run(async () => supabase.from('survey_questions').delete().eq('id', q.id), 'Question deleted.');
  };
  const archive = (q: AdminQuestion) => {
    if (!window.confirm(`Archive “${q.label}”? New travelers won't see it; its ${q.answer_count} answer${q.answer_count === 1 ? '' : 's'} stay in the results.`)) return;
    void run(async () => supabase.from('survey_questions').update({ archived_at: new Date().toISOString() }).eq('id', q.id), 'Question archived.');
  };
  const restore = (q: AdminQuestion) => {
    void run(async () => {
      const position = (active[active.length - 1]?.position ?? 0) + 10;
      return supabase.from('survey_questions').update({ archived_at: null, position }).eq('id', q.id);
    }, 'Question restored at the end.');
  };

  const small = 'text-xs px-2.5 py-1.5 rounded-lg border border-app-border text-app-fg-2 hover:text-app-fg hover:border-app-muted transition-colors disabled:opacity-40';

  function row(q: AdminQuestion, index: number | null) {
    const actions = allowedActions(q);
    return (
      <div key={q.id} className="bg-app-card border border-app-border rounded-xl px-4 py-3">
        <div className="flex items-start gap-3">
          {index !== null && (
            <div className="flex flex-col shrink-0">
              <button type="button" aria-label="Move up" onClick={() => void move(index, -1)} disabled={saving || index === 0} className="text-app-muted hover:text-app-fg disabled:opacity-25 leading-none p-1">▲</button>
              <button type="button" aria-label="Move down" onClick={() => void move(index, 1)} disabled={saving || index === active.length - 1} className="text-app-muted hover:text-app-fg disabled:opacity-25 leading-none p-1">▼</button>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm text-app-fg">{q.label}</div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-[11px] text-app-muted">
              <span>{q.section}</span><span>·</span><span>{KIND_LABEL[q.kind]}</span>
              {q.required && <><span>·</span><span className="text-app-fg-2">required</span></>}
              {q.role && <><span>·</span><span className="text-gold">core, always asked</span></>}
              {q.host_visible && <><span>·</span><span className="text-app-fg-2">shown to host</span></>}
              <span>·</span><span>{q.answer_count} answer{q.answer_count === 1 ? '' : 's'}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {!q.archived_at && (
            <label className="flex items-center gap-1.5 text-xs text-app-fg-2 mr-2">
              <input
                type="checkbox"
                checked={q.required}
                disabled={saving}
                onChange={(e) => void run(async () => supabase.from('survey_questions').update({ required: e.target.checked }).eq('id', q.id))}
              />
              Required
            </label>
          )}
          {actions.includes('edit') && <button type="button" className={small} disabled={saving} onClick={() => { setError(null); setDraft(draftFrom(q, 'edit', q.section)); }}>Edit</button>}
          {actions.includes('replace') && <button type="button" className={small} disabled={saving} onClick={() => { setError(null); setDraft(draftFrom(q, 'replace', q.section)); }}>Replace</button>}
          {actions.includes('archive') && <button type="button" className={small} disabled={saving} onClick={() => archive(q)}>Archive</button>}
          {actions.includes('delete') && <button type="button" className={cn(small, 'text-crimson/80')} disabled={saving} onClick={() => del(q)}>Delete</button>}
          {actions.includes('restore') && <button type="button" className={small} disabled={saving} onClick={() => restore(q)}>Restore</button>}
          {actions.length === 0 && <span className="text-[11px] text-app-muted">Locked: core question with answers.</span>}
        </div>
        {q.answer_count > 0 && !q.archived_at && !q.role && (
          <p className="text-[11px] text-app-muted mt-2">Has answers, so its wording can't change. Replace it to ask a new version.</p>
        )}
      </div>
    );
  }

  // The question being edited or replaced: its form opens where it sits in the list.
  const inlineId = draft ? (draft.editId ?? draft.replaces?.id ?? null) : null;
  const formRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (inlineId) formRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [inlineId]);
  const form = draft && (
    <QuestionForm draft={draft} sections={sections} saving={saving} error={error} onChange={setDraft}
      onSave={() => void save()} onCancel={() => { setDraft(null); setError(null); }} />
  );

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-4">
        <p className="text-sm text-app-muted max-w-xl">
          A question nobody has answered can be edited or deleted. Once it has answers it can only be archived or replaced,
          so past results always match the question travelers saw. The core questions are always asked.
        </p>
        <Button onClick={() => { setError(null); setNotice(null); setDraft(draftFrom(null, 'new', sections[0] ?? 'Your trip')); }} disabled={!!draft}>+ New question</Button>
      </div>

      {notice && <p className="text-sm text-success mb-3" role="status">{notice}</p>}
      {/* A new question's form sits up here by its button; editing or replacing opens it in place of that question's row. */}
      {draft && !inlineId && form}
      {!draft && error && <p className="text-sm text-crimson mb-3" role="alert">{error}</p>}

      <div className="flex flex-col gap-2">
        {active.map((q, i) => (q.id === inlineId ? <div key={q.id} ref={formRef}>{form}</div> : row(q, i)))}
      </div>

      {archived.length > 0 && (
        <div className="mt-8">
          <button type="button" onClick={() => setShowArchived((v) => !v)} className="text-sm font-medium text-app-fg-2 hover:text-app-fg">
            {showArchived ? '▾' : '▸'} Archived ({archived.length})
          </button>
          {showArchived && <div className="flex flex-col gap-2 mt-3 opacity-80">{archived.map((q) => row(q, null))}</div>}
        </div>
      )}
    </div>
  );
}

export default SurveyQuestionEditor;
